// src/pages/Notificaciones/CentroNotificaciones.js
//
// Centro de Notificaciones: todo el correo que sale del portal.
//
// Tres solapas: los envíos (reclamos, órdenes de compra y lo que se agregue),
// las casillas desde las que sale, y el texto de cada uno.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast/ToastProvider';
import { formatFecha } from '../../utils/formato';
import './CentroNotificaciones.css';

const ESTADOS = {
  SENT:    { texto: 'Enviado',  clase: 'text-bg-success' },
  FAILED:  { texto: 'Falló',    clase: 'text-bg-danger' },
  PENDING: { texto: 'En curso', clase: 'text-bg-secondary' }
};

/** Un mes atrás: la pantalla contesta "qué quedó sin salir", y un envío que
 *  falló ayer sigue fallado hoy. Arrancando en hoy abriría vacía. */
const unMesAtras = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

const CentroNotificaciones = () => {
  const { showToast } = useToast();
  const { puedeConfigurar } = useAuth();

  const [solapa, setSolapa] = useState('envios');

  // --- Envíos ---
  const [envios, setEnvios] = useState([]);
  const [totalEnvios, setTotalEnvios] = useState(0);
  const [desde, setDesde] = useState(unMesAtras());
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // --- Configuración ---
  const [cuentas, setCuentas] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [modalPassword, setModalPassword] = useState(null);
  const [modalPlantilla, setModalPlantilla] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargarEnvios = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/notificaciones/envios', {
        params: { desde: desde || undefined, estado: filtroEstado || undefined, limite: 200 }
      });
      setEnvios(data.envios);
      setTotalEnvios(data.total);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los envíos.');
    } finally {
      setCargando(false);
    }
  }, [desde, filtroEstado]);

  useEffect(() => { if (solapa === 'envios') cargarEnvios(); }, [solapa, cargarEnvios]);

  const cargarConfig = useCallback(async () => {
    if (!puedeConfigurar) return;
    try {
      const [c, p] = await Promise.all([
        api.get('/notificaciones/cuentas'),
        api.get('/notificaciones/plantillas')
      ]);
      setCuentas(c.data.cuentas);
      setPlantillas(p.data.plantillas);
      setEventos(p.data.eventos);
      if (!c.data.hayClaveCifrado) {
        showToast('Falta NOTIFICACIONES_KEY en el .env: no se pueden guardar contraseñas.', 'danger', 12000);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'No se pudo leer la configuración.', 'danger');
    }
  }, [puedeConfigurar, showToast]);

  useEffect(() => { if (solapa !== 'envios') cargarConfig(); }, [solapa, cargarConfig]);

  const guardarPassword = async () => {
    setGuardando(true);
    try {
      await api.put(`/notificaciones/cuentas/${modalPassword.id}/password`, { password: modalPassword.password });
      showToast('Contraseña guardada y cifrada.', 'success');
      setModalPassword(null);
      cargarConfig();
    } catch (err) {
      showToast(err.response?.data?.error || 'No se pudo guardar.', 'danger', 9000);
    } finally { setGuardando(false); }
  };

  const probarCuenta = async (id) => {
    showToast('Probando conexión...', 'info');
    try {
      const { data } = await api.post(`/notificaciones/cuentas/${id}/probar`);
      showToast(data.mensaje, data.ok ? 'success' : 'danger', data.ok ? 5000 : 12000);
    } catch (err) {
      showToast('No se pudo probar la cuenta.', 'danger');
    }
  };

  const guardarPlantilla = async () => {
    setGuardando(true);
    try {
      await api.put(`/notificaciones/plantillas/${modalPlantilla.id}`, {
        evento: modalPlantilla.evento,
        asunto: modalPlantilla.asunto,
        cuerpo: modalPlantilla.cuerpo,
        cuentaId: modalPlantilla.cuentaId
      });
      showToast('Plantilla guardada.', 'success');
      setModalPlantilla(null);
      cargarConfig();
    } catch (err) {
      // El backend rechaza los marcadores que el evento no declara.
      showToast(err.response?.data?.error || 'No se pudo guardar.', 'danger', 12000);
    } finally { setGuardando(false); }
  };

  const variablesDe = (evento) => eventos.find(e => e.codigo === evento)?.variables || [];

  return (
    <div className="container-fluid centro-notificaciones">
      <h1 className="mt-4 mb-1">
        <i className="bi bi-envelope-paper me-2"></i>
        Centro de Notificaciones
      </h1>
      <p className="text-muted small">
        Todo el correo que sale del portal: a quién se le mandó, cuándo, y qué pasó con cada intento.
      </p>

      <ul className="nav nav-tabs mt-3">
        <li className="nav-item">
          <button className={`nav-link ${solapa === 'envios' ? 'active' : ''}`} onClick={() => setSolapa('envios')}>
            <i className="bi bi-send me-1"></i> Envíos
          </button>
        </li>
        {puedeConfigurar && (
          <>
            <li className="nav-item">
              <button className={`nav-link ${solapa === 'cuentas' ? 'active' : ''}`} onClick={() => setSolapa('cuentas')}>
                <i className="bi bi-person-badge me-1"></i> Casillas
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${solapa === 'plantillas' ? 'active' : ''}`} onClick={() => setSolapa('plantillas')}>
                <i className="bi bi-file-text me-1"></i> Plantillas
              </button>
            </li>
          </>
        )}
      </ul>

      {/* ================= ENVÍOS ================= */}
      {solapa === 'envios' && (
        <div className="card shadow-sm border-top-0 rounded-top-0">
          <div className="card-body">
            <div className="row g-2 align-items-end mb-3">
              <div className="col-sm-3">
                <label className="form-label small text-muted mb-1">Desde</label>
                <input type="date" className="form-control form-control-sm"
                       value={desde} onChange={e => setDesde(e.target.value)} />
              </div>
              <div className="col-sm-3">
                <label className="form-label small text-muted mb-1">Estado</label>
                <select className="form-select form-select-sm" value={filtroEstado}
                        onChange={e => setFiltroEstado(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="SENT">Enviados</option>
                  <option value="FAILED">Fallados</option>
                  <option value="PENDING">En curso</option>
                </select>
              </div>
              <div className="col-sm-3">
                <button className="btn btn-outline-primary btn-sm" onClick={cargarEnvios} disabled={cargando}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Actualizar
                </button>
              </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {cargando ? (
              <div className="text-center my-4"><div className="spinner-border text-primary"></div></div>
            ) : envios.length === 0 ? (
              <p className="text-muted mb-0">No hay envíos en el período.</p>
            ) : (
              <>
                <div className="tabla-envios">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '104px' }}>Estado</th>
                        <th style={{ width: '124px' }}>Fecha</th>
                        <th>Evento</th>
                        <th>Referencia</th>
                        <th>Destinatario</th>
                        <th>Usuario</th>
                        <th>Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {envios.map(e => {
                        const est = ESTADOS[e.estado] || ESTADOS.PENDING;
                        return (
                          <tr key={e.id}>
                            <td><span className={`badge ${est.clase}`}>{est.texto}</span></td>
                            <td className="small text-nowrap">{formatFecha(e.fecha)}</td>
                            <td className="small">{e.evento}</td>
                            <td className="small">
                              {e.referenciaId ? <code>{e.referenciaId}</code> : '—'}
                              {e.empresa && <span className="text-muted ms-1">{e.empresa}</span>}
                            </td>
                            <td className="small">{e.destinatario}</td>
                            {/* null = lo mandó el sistema, no una persona */}
                            <td className="small text-muted">{e.usuario || <em>sistema</em>}</td>
                            <td className="small text-danger">{e.error || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-muted small mb-0 mt-2">
                  {envios.length} de {totalEnvios} envíos
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ================= CASILLAS ================= */}
      {solapa === 'cuentas' && puedeConfigurar && (
        <div className="card shadow-sm border-top-0 rounded-top-0">
          <div className="card-body">
            <p className="text-muted small">
              Desde qué casilla sale cada tipo de aviso. Una orden de compra sale de Compras;
              un aviso de sistema, de una casilla que nadie contesta.
            </p>
            {cuentas.length === 0 ? (
              <p className="text-muted mb-0">No hay casillas configuradas.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nombre</th><th>Remitente</th><th>Servidor</th>
                      <th>Contraseña</th><th>Estado</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentas.map(c => (
                      <tr key={c.id}>
                        <td className="fw-semibold">{c.nombre}</td>
                        <td className="small">{c.from}</td>
                        <td className="small text-muted">{c.host}:{c.puerto} · {c.seguridad}</td>
                        <td>
                          {c.passwordConfigurada
                            ? <span className="badge text-bg-success"><i className="bi bi-lock-fill me-1"></i>Cargada</span>
                            : <span className="badge text-bg-warning"><i className="bi bi-exclamation-triangle me-1"></i>Falta</span>}
                        </td>
                        <td>
                          <span className={`badge ${c.estado === 'ACTIVA' ? 'text-bg-light border' : 'text-bg-secondary'}`}>
                            {c.estado}
                          </span>
                        </td>
                        <td className="text-end text-nowrap">
                          <button className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() => setModalPassword({ id: c.id, nombre: c.nombre, password: '' })}>
                            <i className="bi bi-key"></i> Contraseña
                          </button>
                          <button className="btn btn-sm btn-outline-secondary"
                                  onClick={() => probarCuenta(c.id)}
                                  disabled={!c.passwordConfigurada}>
                            Probar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="alert alert-secondary small mt-3 mb-0">
              <i className="bi bi-shield-lock me-1"></i>
              Las contraseñas se guardan cifradas y no se pueden volver a leer, ni desde acá
              ni desde la base. Si se pierde una, se carga de nuevo.
            </div>
          </div>
        </div>
      )}

      {/* ================= PLANTILLAS ================= */}
      {solapa === 'plantillas' && puedeConfigurar && (
        <div className="card shadow-sm border-top-0 rounded-top-0">
          <div className="card-body">
            {plantillas.length === 0 ? (
              <p className="text-muted mb-0">No hay plantillas configuradas.</p>
            ) : plantillas.map(p => {
              const def = eventos.find(e => e.codigo === p.evento);
              return (
                <div className="border rounded p-3 mb-3" key={p.id}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <div className="fw-semibold">{def?.etiqueta || p.evento}</div>
                      <div className="text-muted small">{def?.descripcion}</div>
                      <div className="small mt-1">
                        Sale por: {p.cuenta
                          ? <strong>{p.cuenta}</strong>
                          : <span className="text-danger">sin casilla asignada</span>}
                      </div>
                    </div>
                    <button className="btn btn-sm btn-outline-primary"
                            onClick={() => setModalPlantilla({ ...p })}>
                      <i className="bi bi-pencil me-1"></i>Editar
                    </button>
                  </div>
                  <div className="mt-2 small">
                    <div className="text-muted">Asunto</div>
                    <div className="font-monospace">{p.asunto}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* --- Modal: contraseña --- */}
      {modalPassword && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Contraseña de «{modalPassword.nombre}»</h5>
                  <button className="btn-close" onClick={() => setModalPassword(null)}></button>
                </div>
                <div className="modal-body">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Contraseña o clave de aplicación"
                    autoFocus
                    value={modalPassword.password}
                    onChange={e => setModalPassword({ ...modalPassword, password: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && guardarPassword()}
                  />
                  <div className="form-text">
                    Se guarda cifrada. No se puede volver a ver: si hace falta cambiarla, se carga otra.
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setModalPassword(null)} disabled={guardando}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={guardarPassword}
                          disabled={guardando || !modalPassword.password}>
                    {guardando ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setModalPassword(null)}></div>
        </>
      )}

      {/* --- Modal: plantilla --- */}
      {modalPlantilla && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{modalPlantilla.evento}</h5>
                  <button className="btn-close" onClick={() => setModalPlantilla(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Casilla</label>
                    <select className="form-select" value={modalPlantilla.cuentaId || ''}
                            onChange={e => setModalPlantilla({ ...modalPlantilla, cuentaId: Number(e.target.value) || null })}>
                      <option value="">Sin asignar</option>
                      {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.from})</option>)}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Asunto</label>
                    <input type="text" className="form-control font-monospace"
                           value={modalPlantilla.asunto}
                           onChange={e => setModalPlantilla({ ...modalPlantilla, asunto: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Cuerpo</label>
                    <textarea className="form-control font-monospace" rows={10}
                              value={modalPlantilla.cuerpo}
                              onChange={e => setModalPlantilla({ ...modalPlantilla, cuerpo: e.target.value })} />
                  </div>
                  <div className="bloque-marcadores">
                    <div className="small fw-semibold mb-1">Marcadores disponibles</div>
                    <div className="d-flex flex-wrap gap-1">
                      {variablesDe(modalPlantilla.evento).map(v => (
                        <code key={v} className="badge text-bg-light border">{`{{${v}}}`}</code>
                      ))}
                    </div>
                    <div className="form-text">
                      Sólo estos. Cualquier otro se rechaza al guardar: si pasara, el proveedor
                      recibiría el <code>{'{{texto}}'}</code> a la vista.
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setModalPlantilla(null)} disabled={guardando}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={guardarPlantilla} disabled={guardando}>
                    {guardando ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setModalPlantilla(null)}></div>
        </>
      )}
    </div>
  );
};

export default CentroNotificaciones;
